import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../../styles/jira-items.css';
import AITestCaseGenerator from './AITestCaseGenerator';
import { AutomatedTestScenarioGenerator } from './index';
import axios from 'axios';
import axiosInstance from '../../api/axiosInstance';
import useProjectSocket from '../../hooks/useProjectSocket';

import { toast } from 'react-toast';
import { Pagination } from '../ui';
import { TestCasesModal } from './Modals';
import { useLocation } from 'react-router';
import { createPortal } from 'react-dom';
import { useListUrlState } from '../../hooks/useListUrlState';
import { formatDateTime } from '../../utils/dateTimeFormat';
import { parseSourceReference, SourceReferenceBadge } from './SourceReference';

const JIRA_LIST_SCHEMA = {
  tab: 'epics',
  page: 1,
  page_size: 20,
};



  const mapScenarioType = (apiType) => {
    switch (apiType) {
      case 'HAPPY_PATH': return 'happy_path';
      case 'EDGE_CASE': return 'edge_case';
      case 'BDD_PATTERN': return 'bdd';
      default: return 'happy_path';
    }
  };

  const parseRiskImpact = (riskImpact) => {

    if (!riskImpact) return { risk: 0, impact: 0 };

    if (typeof riskImpact === 'object' && 'risk' in riskImpact && 'impact' in riskImpact) {
      return riskImpact;
    }

    if (typeof riskImpact === 'string' && riskImpact.includes(',')) {
      const [risk, impact] = riskImpact.split(',').map(Number);
      return { risk: isNaN(risk) ? 0 : risk, impact: isNaN(impact) ? 0 : impact };
    }

    if (typeof riskImpact === 'string' && riskImpact.includes('/')) {
      const [risk, impact] = riskImpact.split('/').map(Number);
      return { risk: isNaN(risk) ? 0 : risk, impact: isNaN(impact) ? 0 : impact };
    }
    if (typeof riskImpact === 'string') {
      const levelMap = { 'low': 1, 'medium': 2, 'high': 3, 'critical': 4 };
      const val = levelMap[riskImpact.toLowerCase()] ?? 2;
      return { risk: val, impact: val };
    }

    if (typeof riskImpact === 'number') {
      return { risk: riskImpact, impact: riskImpact };
    }

    return { risk: 0, impact: 0 };
  };

  const mapParameterizedSuggestions = (suggestions) => {
    if (!suggestions) return [];

    const result = [];

    if (suggestions.user_roles) {
      result.push({
        parameter: "User Role",
        values: suggestions.user_roles
      });
    }

    if (suggestions.devices) {
      result.push({
        parameter: "Browser/Device",
        values: suggestions.devices
      });
    }

    if (suggestions.credentials) {
      result.push({
        parameter: "Credentials",
        values: suggestions.credentials
      });
    }

    if (suggestions.input_data) {
      result.push({
        parameter: "Input Data",
        values: suggestions.input_data
      });
    }

    return result;
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'done':
        return 'status-done';
      case 'in progress':
        return 'status-in-progress';
      case 'to do':
        return 'status-todo';
      default:
        return 'status-default';
    }
  };

  // Get priority badge class
  const getPriorityClass = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'priority-high';
      case 'medium':
        return 'priority-medium';
      case 'low':
        return 'priority-low';
      default:
        return 'priority-default';
    }
  };



const API_BASE_URL = process.env.REACT_APP_BASE_URL;

const ProjectJiraItems = ({ project, onClose, onNavigateToTestSuite, hideBack = false }) => {

  const location = useLocation()

  const state = location.state || {};

  const integrationSource = state.integrationSource || null;



  const {
    tab: tabParam,
    page: currentPage,
    page_size: pageSize,
    setPage,
    setPageSize,
    setParams,
    pageSizeOptions,
  } = useListUrlState(JIRA_LIST_SCHEMA, { resetPageOn: ['tab', 'page_size'] });

  const activeTab = tabParam === 'stories' ? 'stories' : 'epics';

  const [totalEpics, setTotalEpics] = useState(0);

  // Data state
  const [epics, setEpics] = useState([]);
  const [stories, setStories] = useState([]);
  const [projectKey, setProjectKey] = useState('');
  const [totalStories, setTotalStories] = useState(0);

  // UI state
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [syncProgress, setSyncProgress] = useState(0);
  const [scenarioProgress, setScenarioProgress] = useState(0);
  const [testProgress, setTestProgress] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [expandedEpic, setExpandedEpic] = useState(null);
  const [hasFetchedStories, setHasFetchedStories] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });

  const setActiveTab = useCallback(
    (nextTab) => {
      const tab = nextTab === 'stories' ? 'stories' : 'epics';
      setParams({ tab, page: 1 });
      setExpandedEpic(null);
    },
    [setParams]
  );

  const fetchJiraItems = useCallback(async () => {
    setLoading(true);
    try {
      const authDataString = localStorage.getItem('user');
      if (!authDataString) {
        throw new Error('User not authenticated - no auth data found');
      }

      const authData = JSON.parse(authDataString);
      const accessToken = authData.accessToken;
      if (!accessToken) {
        throw new Error('Access token not found in auth data');
      }
   
      const epicsResponse = await axiosInstance.get(
        `/epics/list?project_id=${project.id}&page=${currentPage}&page_size=${pageSize}`
      );
      if (epicsResponse.epics.length > 0) {
        const keyParts = epicsResponse.epics[0].key.split('-');
        if (keyParts.length > 1) {
          setProjectKey(keyParts[0]);
        }
      }

      const transformedEpics = epicsResponse.epics.map(epic => ({
        id: epic.id,
        key: epic.key,
        summary: epic.summary,
        description: epic.description,
        status: epic.status,
        priority: epic.priority,
        assignee: epic.assignee || 'Unassigned',
        storyPoints: epic.story_point || 0,
        stories: []
      }));

      setEpics(transformedEpics);
      setTotalEpics(epicsResponse?.total_epic_count);
      setTotalStories(epicsResponse?.total_story_count || 0);
      // setStories([]);
    } catch (error) {
      console.error('Error fetching JIRA items:', error);
      toast.error(`Failed to load JIRA items: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [project.id, currentPage, pageSize]);

  const { status: wsStatus, messages: wsMessages, sendEvent, connectWebSocket, progress: wsProgress, waitForConnection, resetProgress, syncEpics,disconnectWebSocket } = useProjectSocket(project.id);

  useEffect(() => {
    console.log('[WebSocket] Connection Status:', wsStatus);
    if (wsStatus === 'connected') {
      //  toast.success('WebSocket connection established');
    } else if (wsStatus === 'disconnected') {
        setIsSyncing(false);
    } else if (wsStatus === 'error') {
      // console.log('[WebSocket] Connection error');
      // toast.error('WebSocket connection error');
      setIsSyncing(false);
    }
  }, [wsStatus]);

  // Update sync progress from WebSocket progress when syncing epics
  useEffect(() => {
    if (isSyncing && wsProgress !== undefined && wsProgress !== null) {
      setSyncProgress(wsProgress);
      if (wsProgress >= 100) {
        setIsSyncing(false);
        disconnectWebSocket()
        setSyncProgress(0);
        toast.success("Epic synchronization completed successfully!");
        fetchJiraItems();
      }
    }
  }, [wsProgress, isSyncing, fetchJiraItems]);

  // Also handle progress from messages as fallback
  useEffect(() => {
    if (wsMessages.length > 0 && isSyncing) {
      const latestMessage = wsMessages[wsMessages.length - 1];
      // Check if message contains sync-related progress
      if (latestMessage.message && latestMessage.message.progress !== undefined) {
        const messageProgress = Number(latestMessage.message.progress);
        // Only update if wsProgress didn't update (fallback)
        if (messageProgress !== wsProgress) {
          setSyncProgress(messageProgress);
          if (messageProgress >= 100) {
            disconnectWebSocket();
            setIsSyncing(false);
            setSyncProgress(0);
            toast.success("Epic synchronization completed successfully!");
            fetchJiraItems();  
          }
        }
      }
    }
  }, [wsMessages, isSyncing, wsProgress, fetchJiraItems]);

  useEffect(() => {
    if (wsMessages.length > 0) {
      const latestMessage = wsMessages[wsMessages.length - 1];
 
      if (latestMessage.sender === 'server' && latestMessage.message) {
         const { project_id, progress, message } = latestMessage.message;
         
        if (typeof progress === 'number' && project_id === String(project.id)) {
          if (message && message.toLowerCase().includes('test')) {
            setTestProgress(Number(progress));
            if (progress >= 100) {
              setLoadingAllTests(false);
              toast.success('All tests generated successfully!');
              fetchJiraItems();
            }
          } else if (message && message.toLowerCase().includes('scenario')) {
            setScenarioProgress(Number(progress));
            if (progress >= 100) {
              setLoadingAllScenarios(false);
              toast.success('All scenarios generated successfully!');
              fetchJiraItems();
            }
          }
        }
        return;
      }

      // Handle event-based messages
      switch (latestMessage.event) {
        case 'epic_sync_complete':
          if (isSyncing) {
            fetchJiraItems();
            setIsSyncing(false);
            setSyncProgress(100);
            toast.success('Epic synchronization completed successfully!');
          }
          break;
        case 'story_sync_complete':
          if (expandedEpic) {
            fetchStoriesForEpic(expandedEpic, currentPage, pageSize);
          }
          toast.success('Stories synchronization completed');
          break;
        case 'test_case_generated':
          setSavedTestCases(prev => [...prev, latestMessage.data.test_case]);
          break;
        case 'batch_progress':
          setBatchGenerationProgress(latestMessage.data.progress);
          break;
        case 'scenario_generation_progress':
          setScenarioProgress(latestMessage.data.progress);
          if (latestMessage.data.progress >= 100) {
            setLoadingAllScenarios(false);
            toast.success('All scenarios generated successfully!');
            fetchJiraItems();
          }
          break;
        default:
          console.log('[WS] Unhandled message:', latestMessage);
      }
    }
  }, [wsMessages, project.id, fetchJiraItems, currentPage, expandedEpic, pageSize, isSyncing]);


  // UI Features state
  const [selectedItems, setSelectedItems] = useState([]);
  const [showTestCaseGenerator, setShowTestCaseGenerator] = useState(false);
  const [showScenarioGenerator, setShowScenarioGenerator] = useState(false);
  const [isBatchGeneration, setIsBatchGeneration] = useState(false);
  const [savedTestCases, setSavedTestCases] = useState([]);
  const [projectId, setProjectId] = useState([project.id]);
  const [loadingEpics, setLoadingEpics] = useState({});
  const [loadingAllScenarios, setLoadingAllScenarios] = useState(false);
  const [loadingAllTests, setLoadingAllTests] = useState(false);
  const [showTestCasesModal, setShowTestCasesModal] = useState(false);
  const [testCasesData, setTestCasesData] = useState(null);
  const [viewTestsStoryId, setViewTestsStoryId] = useState(null);
  const [showEpicDropdown, setShowEpicDropdown] = useState(false);
  const [selectedEpicForSync, setSelectedEpicForSync] = useState('');
  const dropdownRef = useRef(null);
  const syncBtnRef = useRef(null);

  // Calculate total pages
  const totalEpicPages = Math.max(1, Math.ceil(totalEpics / pageSize) || 1);
  const totalStoryPages = Math.max(1, Math.ceil(totalStories / pageSize) || 1);

  const [batchGenerationProgress, setBatchGenerationProgress] = useState({
    total: 0,
    completed: 0,
    inProgress: false
  });

  // Epics: server-paginated; re-fetch when page/size change while on Epics tab
  useEffect(() => {
    if (project?.id && activeTab === 'epics') {
      fetchJiraItems();
    }
  }, [project?.id, activeTab, currentPage, pageSize, fetchJiraItems]);

  // Stories: server-paginated; URL tab/page survive refresh
  useEffect(() => {
    if (project?.id && activeTab === 'stories') {
      fetchStories(currentPage);
      setHasFetchedStories(true);
    }
  }, [project?.id, activeTab, currentPage, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchInitialStoriesForEpics = async () => {
      if (project && project.id && projectKey && epics.length > 0) {
        try {
          // Fetch stories for all epics on initial load
          for (const epic of epics) {
            const epicStories = await fetchStoriesForEpic(epic.key, 1, pageSize);
            setStories(prevStories => {
              // Remove existing stories for this epic and add new ones
              const otherStories = prevStories.filter(story => story.epicId !== epic.key);
              return [...otherStories, ...epicStories];
            });
          }
        } catch (error) {
          console.error('Error fetching initial stories for epics:', error);
        }
      }
    };

    // fetchInitialStoriesForEpics();
  }, [project, projectKey, epics.length]);


  const handleGenerateAllScenarios = async () => {
    try {
      setLoadingAllScenarios(true);
      setScenarioProgress(0);

      connectWebSocket();

      let retries = 0;
      while (wsStatus !== "connected" && retries < 5) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        retries++;
      }


      if (wsStatus === "connected") {
        console.log("[WebSocket] Sending ping event for project:", project.id);
        sendEvent("ping", {
          project_id: project.id,
          message: {
            timestamp: new Date().toISOString()
          }
        });
        
        // Send the actual scenario generation event after ping
        setTimeout(() => {
          console.log("[WebSocket] Sending generate_all_scenarios event for project:", project.id);
          sendEvent("generate_all_scenarios", {
            project_id: project.id,
            progress: 0,
            message: "Starting scenario generation...",
          });
        }, 500);
        
        toast.info("Scenario generation started via WebSocket...");
      } else {
        // Fallback to HTTP if WebSocket is not available
        const authDataString = localStorage.getItem("user");
        if (!authDataString) throw new Error("User not authenticated - no auth data found");
        const authData = JSON.parse(authDataString);
        const accessToken = authData.accessToken;
        
        const response = await axiosInstance.post(
          `/scenario/ai_gen_scenario_project?project_id=${project.id}`,
           {
            headers: {
              token: accessToken,
             },
          }
        );
console.log(response, "this is response");

        if (response.status_code === 202) {
          toast.success("Started generating scenarios for all epics!");
        } else {
          throw new Error(`Unexpected response: ${response.statusText}`);
        }
      }
    } catch (error) {
      console.error("Error generating scenarios:", error);
      toast.error(`Failed to generate scenarios: ${error.message}`);
    } finally {
      if (!wsStatus) {
        setLoadingAllScenarios(false);
      }
    }
  };

  const handleSyncJira = async () => {
    try {
      setIsSyncing(true);
      setSyncProgress(0);
      resetProgress(); // Reset WebSocket progress

      // First, connect the socket and wait for connection
      try {
        await waitForConnection(10000); // Wait up to 10 seconds
        console.log("[WebSocket] Socket connected, calling sync-jira-epics API for project:", project.id);
        
        // Get auth token
        const authDataString = localStorage.getItem("user");
        if (!authDataString) throw new Error("User not authenticated - no auth data found");
        const authData = JSON.parse(authDataString);
        const accessToken = authData.accessToken;
        if (!accessToken) throw new Error("Access token not found in auth data");
        
        const config = {
          headers: {
            token: accessToken,
            "Content-Type": "application/json",
          },
        };

        // Call the API after socket is connected
        const response = await axios.post(
          `${API_BASE_URL}/epics/sync-jira-epics/${project.id}`,
          {},
          config
        );

        if (response.status === 200 || response.status === 202) {
          toast.info("Epic synchronization started. Progress will be shown below...");
          // Progress will be updated via WebSocket messages
        } else {
          throw new Error(`Unexpected response: ${response.statusText}`);
        }
      } catch (connectionError) {
        // If socket connection fails, fall back to HTTP without progress
        console.warn("[WebSocket] Connection failed, falling back to HTTP:", connectionError.message);
        const authDataString = localStorage.getItem("user");
        if (!authDataString) throw new Error("User not authenticated - no auth data found");
        const authData = JSON.parse(authDataString);
        const accessToken = authData.accessToken;
        if (!accessToken) throw new Error("Access token not found in auth data");
        const config = {
          headers: {
            token: accessToken,
            "Content-Type": "application/json",
          },
        };
        const response = await axios.post(
          `${API_BASE_URL}/epics/sync-jira-epics/${project.id}`,
          {},
          config
        );
        if (response.status === 200) {
          toast.success(response.data.message || "JIRA epics synced successfully!");
          fetchJiraItems();
          setIsSyncing(false);
        } else {
          throw new Error(`Unexpected response: ${response.statusText}`);
        }
      }
    } catch (error) {
      console.error("Error syncing JIRA epics:", error);
      setIsSyncing(false);
      setSyncProgress(0);
      if (error.response) {
        toast.error(
          `Failed to sync JIRA epics: ${error.response.data.detail || error.response.statusText}`
        );
      } else if (error.request) {
        toast.error("Failed to sync JIRA epics: No response from server");
      } else {
        toast.error(`Failed to sync JIRA epics: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };


  const fetchStoriesForEpic = async (epicKey, page = 1, maxResults = 50) => {
    try {
      const authDataString = localStorage.getItem('user');
      if (!authDataString) {
        throw new Error('User not authenticated - no auth data found');
      }

      const authData = JSON.parse(authDataString);
      const accessToken = authData.accessToken;
      if (!accessToken) {
        throw new Error('Access token not found in auth data');
      }

      const config = {
        headers: {
          'token': accessToken,
          'Content-Type': 'application/json'
        }
      };

      const startAt = (page - 1) * maxResults;

      const response = await axiosInstance.get(
        `/epics/projects/${project.id}/epics/${epicKey}/stories?start_at=${startAt}&max_results=${maxResults}`)

      return response?.stories.map(story => ({
        id: story.key,
        dbId: story.story_id,
        key: story.key,
        summary: story.summary,
        description: story.description,
        status: story.status,
        priority: story.priority,
        assignee: story.assignee || 'Unassigned',
        storyPoints: story.story_points || 0,
        epicId: story.epic_key,
        epicSummary: story.epic_summary,
        scenario_generated: story.scenario_generated || false,
        case_generated: story.case_generated || false
      }));
    } catch (error) {
      console.error('Error fetching stories for epic:', error);
      toast.error(`Failed to load stories: ${error.message}`);
      return [];
    }
  };

  const fetchAllStories = async (page = 1) => {
    try {
      const authDataString = localStorage.getItem('user');
      if (!authDataString) {
        throw new Error('User not authenticated - no auth data found');
      }

      const authData = JSON.parse(authDataString);
      const accessToken = authData.accessToken;
      if (!accessToken) {
        throw new Error('Access token not found in auth data');
      }

      const config = {
        headers: {
          'token': accessToken,
          'Content-Type': 'application/json'
        }
      };

      const response = await axiosInstance.get(
        `/jira_data/stories?project_id=${project.id}&page=${page}&page_size=${pageSize}`
      );

      // Save total stories for pagination
      setTotalStories(response?.count);


      return response?.stories.map(story => {

        const epic = epics.find(e => e.id === story.jira_epic_id);
        return {
          id: story.id,
          dbId: story.id,
          key: story.story_key,
          summary: story.summary,
          description: story.description,
          status: story.status,
          priority: story.priority,
          assignee: story.assignee || 'Unassigned',
          storyPoints: story.story_points || 0,
          epicId: epic ? epic.epic_key : null,
          epicKey: story.epic_key,
          jiraEpicId: story.jira_epic_id,
          scenario_generated: story.scenario_generated || false,
          case_generated: story.case_generated || false
        };
      });
    } catch (error) {
      console.error('Error fetching stories:', error);
      toast.error(`Failed to load stories: ${error.message}`);
      return [];
    }
  };

  const fetchStories = async (page = 1) => {
    setLoading(true);
    try {
      const allStories = await fetchAllStories(page);
      setStories(allStories);
    } catch (error) {
      console.error('Error fetching stories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncStories = async (epicKey = null) => {
    try {
      setLoading(true);

      const authDataString = localStorage.getItem('user');
      if (!authDataString) {
        throw new Error('User not authenticated - no auth data found');
      }

      const authData = JSON.parse(authDataString);
      const accessToken = authData.accessToken;
      if (!accessToken) {
        throw new Error('Access token not found in auth data');
      }

      const config = {
        headers: {
          'token': accessToken,
          'Content-Type': 'application/json'
        }
      };


      let url = `${API_BASE_URL}/epics/sync-jira-stories/${project.id}`;
      if (epicKey) {
        url += `?epic_key=${epicKey}`;
      }

      const response = await axios.post(url, {}, config);

      if (response.status === 200) {
        toast.success(response.data.message || 'Jira stories synced successfully!');

        fetchStories();
      }
    } catch (error) {
      console.error('Error syncing JIRA stories:', error);
      if (error.response) {
        toast.error(`Failed to sync JIRA stories: ${error.response.data.detail || error.response.statusText}`);
      } else if (error.request) {
        toast.error('Failed to sync JIRA stories: No response from server');
      } else {
        toast.error(`Failed to sync JIRA stories: ${error.message}`);
      }
    } finally {
      setLoading(false);
      setShowEpicDropdown(false);
      setSelectedEpicForSync('');
    }
  };

  // List page change — URL-backed so refresh keeps tab + page
  const handlePageChange = (page) => {
    setPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleGenerateEpicScenarios = async (epicId, epic) => {

    try {
      setLoading(true);

      const authDataString = localStorage.getItem('user');
      if (!authDataString) {
        throw new Error('User not authenticated - no auth data found');
      }
      const authData = JSON.parse(authDataString);
      const accessToken = authData.accessToken;
      if (!accessToken) {
        throw new Error('Access token not found in auth data');
      }

      const config = {
        headers: {
          'token': accessToken,
          'Content-Type': 'application/json'
        }
      };


      const response = await axios.post(
        `${API_BASE_URL}/scenario/ai_gen_scenario_epic?project_id=${project.id}&epic_id=${epicId?.
          id}`,
        {},
        config
      );

      if (response.data?.status_code == 202) {
        toast.success(response.data?.message || 'Scenario generation for epic started in background.');
      } else {
        toast.error('Unexpected response from server');
      }
    } catch (error) {
      console.error('Error generating epic scenarios:', error);
      if (error.response) {
        toast.error(`Failed to generate epic scenarios: ${error.response.data?.detail || error.response.statusText}`);
      } else if (error.request) {
        toast.error('Failed to generate epic scenarios: No response from server');
      } else {
        toast.error(`Failed to generate epic scenarios: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };



  const handleGenerateStoryScenarios = async (story) => {
    try {
      setLoading(true);
      const authDataString = localStorage.getItem('user');
      if (!authDataString) {
        throw new Error('User not authenticated - no auth data found');
      }

      const authData = JSON.parse(authDataString);
      const accessToken = authData.accessToken;
      if (!accessToken) {
        throw new Error('Access token not found in auth data');
      }

      const config = {
        headers: {
          'token': accessToken,
          'Content-Type': 'application/json'
        }
      };

      const storyId = story.dbId || story.id;

      if (!storyId || isNaN(storyId)) {
        throw new Error('Valid story ID not found. Please sync stories first.');
      }

      // First call the generation API
      const generateResponse = await axios.post(
        `${API_BASE_URL}/scenario/ai_gen_scenario_story?story_id=${storyId}`,
        {},
        config
      );

      if (generateResponse.status === 200 || generateResponse.status_code === 201) {
        // toast.success(generateResponse.data.message || 'Scenarios generated successfully!');

        const fetchResponse = await axiosInstance.get(
          `/scenario/?project_id=${project.id}&story_id=${storyId}`
        );

        if (fetchResponse.status_code === 200 || fetchResponse.status === 200) {
          // Transform the API response to match our expected format
          const transformedScenarios = fetchResponse?.scenarios.map(scenario => ({
            id: scenario.id,
            title: scenario.scenario_name,
            description: scenario.scenario_details?.scenario_summary || scenario.scenario_name,
            type: mapScenarioType(scenario.scenario_details?.scenario_type),
            preconditions: scenario.scenario_details?.preconditions || [],
            steps: scenario.scenario_details?.steps ? [
              { type: 'given', text: scenario.scenario_details.steps.given },
              { type: 'when', text: scenario.scenario_details.steps.when },
              { type: 'then', text: scenario.scenario_details.steps.then }
            ] : [],
            expectedResult: scenario.scenario_details?.expected_result || '',
            priority: scenario.scenario_details?.priority || 'Medium',
            riskImpact: parseRiskImpact(scenario.scenario_details?.risk_impact),
            estimatedTime: scenario.scenario_details?.estimated_time || '30 minutes',
            source: scenario.scenario_details?.source || 'AI Generated',
            parameterizedSuggestions: mapParameterizedSuggestions(scenario.scenario_details?.parameterized_test_suggestions)
          }));

          // Set the selected item with generated scenarios
          setSelectedItem({
            ...story,
            generatedScenarios: transformedScenarios
          });
          setShowScenarioGenerator(true);
        }
      } else {
        toast.error('Failed to generate scenarios for story');
      }
    } catch (error) {
      console.error('Error generating story scenarios:', error);
      if (error.response) {
        if (error.response.status === 500 && error.response.data?.detail?.message === "AI response not in valid JSON format") {
          toast.error('AI response format error. Please try again or contact support.');
        } else {
          toast.error(`Failed to generate story scenarios: ${error.response.data?.detail || error.response.statusText}`);
        }
      } else if (error.request) {
        toast.error('Failed to generate story scenarios: No response from server');
      } else {
        toast.error(`Failed to generate story scenarios: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };




  const handleViewTests = async (story) => {
    try {
      setLoading(true);

      const authDataString = localStorage.getItem('user');
      if (!authDataString) {
        throw new Error('User not authenticated');
      }

      const authData = JSON.parse(authDataString);
      const accessToken = authData.accessToken;
      if (!accessToken) {
        throw new Error('Access token not found');
      }

      const config = {
        headers: {
          'token': accessToken,
          'Content-Type': 'application/json'
        }
      };


      const storyId = story.dbId || story.id;

      if (!storyId || isNaN(storyId)) {
        throw new Error('Valid story ID not found');
      }

      const response = await axiosInstance.get(
        `/test_case/cases_list?project_id=${project.id}&page=1&page_size=10&jira_story_id=${storyId}`,
        config
      );
      if (response.status === 200 || response.status == "success") {
        setViewTestsStoryId(storyId);
        setTestCasesData(response);
        setShowTestCasesModal(true);
      }
    } catch (error) {
      console.error('Error fetching test cases:', error);
      toast.error(`Failed to fetch test cases: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleViewScenarios = async (story) => {
    try {
      setLoading(true);

      const authDataString = localStorage.getItem('user');
      if (!authDataString) {
        throw new Error('User not authenticated');
      }

      const authData = JSON.parse(authDataString);
      const accessToken = authData.accessToken;
      if (!accessToken) {
        throw new Error('Access token not found');
      }

      const config = {
        headers: {
          'token': accessToken,
          'Content-Type': 'application/json'
        }
      };

      // Get the story ID from the database
      const storyId = story.dbId || story.id;

      if (!storyId || isNaN(storyId)) {
        throw new Error('Valid story ID not found');
      }

      const response = await axiosInstance.get(
        `/scenario/?project_id=${project.id}&story_id=${storyId}`
      );
      if (response.status_code === 200) {
        // Transform the API response to match our expected format
        const transformedScenarios = response.scenarios.map(scenario => ({
          id: scenario.id,
          title: scenario.scenario_name,
          description: scenario.scenario_details?.scenario_summary || scenario.scenario_name,
          type: mapScenarioType(scenario.scenario_details?.scenario_type),
          preconditions: scenario.scenario_details?.preconditions || [],
          steps: scenario.scenario_details?.steps ? [
            { type: 'given', text: scenario.scenario_details.steps.given },
            { type: 'when', text: scenario.scenario_details.steps.when },
            { type: 'then', text: scenario.scenario_details.steps.then }
          ] : [],
          expectedResult: scenario.scenario_details?.expected_result || '',
          priority: scenario.scenario_details?.priority || 'Medium',
          riskImpact: parseRiskImpact(scenario.scenario_details?.risk_impact),
          estimatedTime: scenario.scenario_details?.estimated_time || '30 minutes',
          source: scenario.scenario_details?.source || 'AI Generated',
          parameterizedSuggestions: mapParameterizedSuggestions(scenario.scenario_details?.parameterized_test_suggestions)
        }));


        setSelectedItem({
          ...story,
          generatedScenarios: transformedScenarios
        });
        setShowScenarioGenerator(true);
      }
    } catch (error) {
      console.error('Error fetching scenarios:', error);
      toast.error(`Failed to fetch scenarios: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateTestCases = (item, itemType) => {
    setSelectedItem(item);
    setShowTestCaseGenerator(true);
  };

  // Handle generating test scenarios for an epic or story
  const handleGenerateTestScenarios = (item, itemType) => {
    setSelectedItem(item);
    setShowScenarioGenerator(true);
  };

  // Handle saving generated test cases
  const handleSaveTestCases = (testCases, item, itemType) => {
    console.log(`Saving ${testCases.length} test cases for ${itemType} ${item.id}`);

    setSavedTestCases([
      ...savedTestCases,
      ...testCases.map(tc => ({
        ...tc,
        sourceItem: item,
        sourceItemType: itemType,
        createdAt: new Date().toISOString()
      }))
    ]);

    if (isBatchGeneration) {
      const currentItemIndex = selectedItems.findIndex(
        si => si.item.id === item.id && si.type === itemType
      );

      if (currentItemIndex < selectedItems.length - 1) {
        const nextItem = selectedItems[currentItemIndex + 1];
        setSelectedItem(nextItem.item);
      } else {
        setShowTestCaseGenerator(false);
        setIsBatchGeneration(false);
        toast.success(`Successfully generated test cases for all ${selectedItems.length} items!`);
      }
    } else {
      setShowTestCaseGenerator(false);
    }
  };

  // Handle saving generated test scenarios
  const handleSaveTestScenarios = (scenarios, item, itemType) => {
    console.log(`Saving ${scenarios.length} test scenarios for ${itemType} ${item.id}`);

    setSavedTestCases([
      ...savedTestCases,
      ...scenarios.map(scenario => ({
        ...scenario,
        sourceItem: item,
        sourceItemType: itemType,
        createdAt: new Date().toISOString(),
        scenarioType: scenario.type
      }))
    ]);

    setShowScenarioGenerator(false);
  };

  // Handle generating tests for all EPICs and their stories
  const handleGenerateAllTests = async () => {
    try {
      setLoadingAllTests(true);
      setTestProgress(0);

      connectWebSocket();
      
      try {
        await waitForConnection(3000);

        // WebSocket is connected, send events
        console.log("[WebSocket] Sending ping event for project:", project.id);
        sendEvent("ping", {
          project_id: project.id,
          message: {
            timestamp: new Date().toISOString()
          }
        });
        
        // Send the actual test generation event after ping
        setTimeout(() => {
          console.log("[WebSocket] Sending sync_epics event for project:", project.id);
          sendEvent("sync_epics", {
            project_id: project.id,
            progress: 0,
            message: "Starting test generation..."
          });
        }, 1000);
        const response = await axiosInstance.post(
          `/test_case/gen_project_test_cases?project_id=${project.id}`
        );

        if (response.status === 200) {
          toast.success("Started generating tests for all items!");
        } else {
          throw new Error(`Unexpected response: ${response.statusText}`);
        }

        
        // toast.info("Test generation started via WebSocket...");
      } catch (wsError) {
        console.warn("[WebSocket] Connection failed, falling back to HTTP:", wsError.message);
        setLoadingAllTests(false);

        const authDataString = localStorage.getItem("user");
        if (!authDataString) {
          throw new Error("User not authenticated - no auth data found");
        }
        
      }
    } catch (error) {
      console.error('Error generating all tests:', error);
      setLoadingAllTests(false);
      if (error.response) {
        toast.error(`Failed to generate tests: ${error.response.data?.detail || error.response.statusText}`);
      } else if (error.request) {
        toast.error('Network error occurred while generating tests');
      } else {
        toast.error('Failed to generate tests: ' + error.message);
      }
    }
  };

  const generateTestCasesForStory = async (storyKey) => {
    try {
      const authDataString = localStorage.getItem('user');
      if (!authDataString) {
        throw new Error('User not authenticated');
      }

      const authData = JSON.parse(authDataString);
      const accessToken = authData.accessToken;
      if (!accessToken) {
        throw new Error('Access token not found');
      }

      const config = {
        headers: {
          'token': accessToken,
          'Content-Type': 'application/json'
        }
      };

      const response = await axios.post(
        `${API_BASE_URL}/test_case/gen_ai_test_cases_steps?project_id=${project.id}&story_key=${storyKey}`,
        {},
        config
      );

      return response.data;
    } catch (error) {
      console.error(`Error generating test cases for story ${storyKey}:`, error);
      throw error;
    }
  };

  // Get stories for a specific epic
  const getStoriesForEpic = (epicId) => {
    return stories.filter(story => story.epicId === epicId);
  };

  // Toggle epic expansion
  const toggleEpic = async (epicId) => {
    if (expandedEpic === epicId) {
      setExpandedEpic(null);
    } else {
      setLoadingEpics(prev => ({ ...prev, [epicId]: true }));

      try {
        const epicStories = await fetchStoriesForEpic(epicId, 1, pageSize);
        const filteredStories = epicStories.filter(story => story.epicId === epicId);

        setStories(prevStories => {
          const otherStories = prevStories.filter(story => story.epicId !== epicId);
          return [...otherStories, ...filteredStories];
        });

        setExpandedEpic(epicId);
      } catch (error) {
        console.error('Error toggling epic:', error);
      } finally {
        setLoadingEpics(prev => ({ ...prev, [epicId]: false }));
      }
    }
  };

  
  const handleScenarioGeneratorClose = useCallback(() => {
  setShowScenarioGenerator(false);
  if (activeTab === 'stories') {
    setHasFetchedStories(false);
    fetchStories(currentPage); // currentPage is fresh here via dependency array
  }
}, [activeTab, currentPage]);

  const hasEpics = totalEpics > 0;
  const hasStories = totalStories > 0;
  const hasAnyWorkItems = hasEpics || hasStories;

  const canGenerateAllScenarios = hasAnyWorkItems && !loadingAllScenarios;
  const canGenerateAllTests = hasStories && !loadingAllTests;

  const generateAllScenariosTitle = !hasAnyWorkItems
    ? 'No epics or stories available to generate scenarios'
    : 'Generate scenarios for all epics and stories';
  const generateAllTestsTitle = !hasStories
    ? hasEpics
      ? 'No stories available — tests are generated per story'
      : 'No epics or stories available to generate tests'
    : 'Generate tests for all stories';

  
  if (showScenarioGenerator && selectedItem) {
    return (
      <div className="jira-items-overlay rt-jira-host">
        <AutomatedTestScenarioGenerator
          item={selectedItem}
          itemType={selectedItem.epicId ? 'story' : 'epic'}
          onClose={handleScenarioGeneratorClose}
          onSave={handleSaveTestScenarios}
          onNavigateToTestSuite={onNavigateToTestSuite}
          generatedScenarios={selectedItem.generatedScenarios}
          projectId={project.id}
          caseGenerated={selectedItem.case_generated || false}
        />
        {showTestCasesModal && (
          <TestCasesModal
            testCasesData={testCasesData}
            onClose={() => setShowTestCasesModal(false)}
            title="Test Cases"
            projectId={project.id}
            storyId={viewTestsStoryId}
            isServerSide={true}
          />
        )}
      </div>
    );
  }

  if (showTestCaseGenerator && selectedItem) {
    return (
      <div className="jira-items-overlay rt-jira-host">
        <AITestCaseGenerator
          item={selectedItem}
          itemType={selectedItem.epicId ? 'story' : 'epic'}
          onClose={() => setShowTestCaseGenerator(false)}
          onSave={handleSaveTestCases}
          isBatchGeneration={isBatchGeneration}
          projectId={projectId}
        />
      </div>
    );
  }

  return (
    <div className="jira-items-overlay rt-jira-host">
      <div className="jira-items-container">
        {!hideBack && typeof onClose === 'function' && (
          <button
            className="back-btn"
            onClick={onClose}
            title="Back"
            type="button"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        )}

        <div className={`jira-items-tabs${hideBack ? ' rt-define-jira-toolbar' : ''}`}>
          <div className="rt-define-jira-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'epics'}
              className={`tab-btn ${activeTab === 'epics' ? 'active' : ''}`}
              onClick={() => setActiveTab('epics')}
            >
              Epics ({totalEpics})
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'stories'}
              className={`tab-btn ${activeTab === 'stories' ? 'active' : ''}`}
              onClick={() => setActiveTab('stories')}
            >
              Stories ({totalStories})
            </button>
          </div>
          <div className="rt-define-jira-actions">
          <button
            type="button"
            className={`generate-all-scenarios-btn${loadingAllScenarios && scenarioProgress < 100 ? ' is-loading' : ''}`}
            onClick={handleGenerateAllScenarios}
            title={generateAllScenariosTitle}
            disabled={!canGenerateAllScenarios}
          >
            {loadingAllScenarios && scenarioProgress < 100 ? (
              <>
                <i className='fas fa-spinner fa-spin'></i>
                <span>Generating Scenarios... {Math.round(scenarioProgress)}%</span>
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"></path>
                </svg>
                Generate All Scenarios
              </>
            )}
          </button>
          <button
            type="button"
            className={`generate-all-tests-btn${loadingAllTests && testProgress < 100 ? ' is-loading' : ''}`}
            onClick={handleGenerateAllTests}
            title={generateAllTestsTitle}
            disabled={!canGenerateAllTests}
          >
            {loadingAllTests && testProgress < 100 ? (
              <>
                <i className='fas fa-spinner fa-spin'></i>
                <span>Generating Tests... {Math.round(testProgress)}%</span>
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"></path>
                </svg>
                Generate All Tests
              </>
            )}
          </button>
          </div>
        </div>

        <div className="jira-items-content">
          {loading ? (
            <div className="card-stat" style={{ justifySelf: 'center' }}>
              <i className='fas fa-spinner fa-spin'></i>
            </div>
          ) : (
            <>
              {activeTab === 'epics' && (
                <div className="epics-list">
                  <div className="pagination-info" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
                    {isSyncing && (
                      <>
                        <div className="sync-progress-container">
                          <div className="sync-progress-bar" style={{ width: `${syncProgress}%` }} />
                        </div>
                        <div style={{ textAlign: 'right', fontSize: '0.875rem', color: '#666', marginTop: '0.25rem', }} >
                          {syncProgress}%
                        </div>
                      </>
                    )}
                 {integrationSource !==3 &&     <button
                      className="sync-epic-btn"
                      onClick={() => {
                        setIsSyncing(true);
                        setSyncProgress(0);
                        handleSyncJira();
                      }}
                      title="Sync Jira Data"
                      disabled={loading || isSyncing}
                    >
                      {isSyncing ? (
                        <>
                          <i className='fas fa-spinner fa-spin'></i>
                          Syncing...
                        </>
                      ) : (
                        <>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 1l4 4-4 4"></path>
                            <path d="M3 11V9a4 4 0 014-4h14"></path>
                            <path d="M7 23l-4-4 4-4"></path>
                            <path d="M21 13v2a4 4 0 01-4 4H3"></path>
                          </svg>
                          Sync Epics
                        </>
                      )}
                    </button>}
                  </div>
                  {epics.length > 0 ? (
                    <>

                      {epics.map(epic => (
                        <div key={epic.key} className="epic-item" onClick={() => toggleEpic(epic.key)}>
                          <div className="epic-header">
                            <div className="epic-title">
                              <span className="jira-key">{epic.key}</span>
                              <span className="epic-summary">{epic.summary}</span>
                            </div>
                            <div className="epic-meta">
                              <div className="epic-meta-info">
                                <span className={`status-badge ${getStatusClass(epic.status)}`}>
                                  {epic.status}
                                </span>
                                <span className={`priority-badge ${getPriorityClass(epic.priority)}`}>
                                  {epic.priority}
                                </span>
                                {epic.storyPoints > 0 && (
                                  <span className="story-points">{epic.storyPoints} SP</span>
                                )}
                              </div>
                              <div className="epic-action-buttons">
                                <button
                                  className="generate-tests-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleGenerateTestCases(epic, 'epic');
                                  }}
                                  title="Generate AI Test Cases"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"></path>
                                  </svg>
                                  Generate Tests
                                </button>
                                <button
                                  className="generate-scenarios-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleGenerateEpicScenarios(epic, 'epic');
                                  }}
                                  title="Generate BDD Test Scenarios"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                                  </svg>
                                  Generate Scenarios
                                </button>
                              </div>
                              {loadingEpics[epic.key] ? (
                                <span className="expand-icon">
                                  <i className='fas fa-spinner fa-spin'></i>
                                </span>
                              ) : (
                                <span className="expand-icon" onClick={() => toggleEpic(epic.key)}>
                                  {expandedEpic === epic.key ? '▼' : '▶'}
                                </span>
                              )}
                            </div>
                          </div>

                          {expandedEpic === epic.key && (
                            <div className="epic-details">
                              <div className="epic-description">
                                <p>{epic.description}</p>
                              </div>
                              {epic.assignee && (
                                <div className="epic-assignee">
                                  <strong>Assignee:</strong> {epic.assignee}
                                </div>
                              )}

                              <div className="epic-stories">
                                <h4>Stories ({getStoriesForEpic(epic.key).length})</h4>
                                {getStoriesForEpic(epic.key).length > 0 ? (
                                  <ul className="story-list">
                                    {getStoriesForEpic(epic.key).map(story => {
                                      const { referenceId, description } = parseSourceReference(story.description);
                                      return (
                                      <li key={story.key} className="story-item">
                                        <div className="story-header">
                                          <span className="jira-key">{story.key}</span>
                                          <span className="story-summary">{story.summary}</span>
                                          <SourceReferenceBadge referenceId={referenceId} />
                                        </div>
                                        <div>
                                          {description}
                                        </div>
                                        <div className="story-meta" style={{ justifySelf: 'start', marginTop: '2%' }}>
                                          <span className={`status-badge ${getStatusClass(story.status)}`}>{story.status}</span>
                                          <span className="story-points">{story.storyPoints} SP</span>
                                          <div className="story-action-buttons small">
                                            {story && story.case_generated ? (
                                              <button
                                                className="view-tests-btn small"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleViewTests(story);
                                                }}
                                                title="View Generated Tests"
                                              >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                  <circle cx="12" cy="12" r="3"></circle>
                                                </svg>
                                                View Tests
                                              </button>
                                            ) : (
                                              <button
                                                className="generate-tests-btn small"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleGenerateTestCases(story, 'story');
                                                }}
                                                title="Generate AI Test Cases"
                                              >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                  <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"></path>
                                                </svg>
                                                Tests
                                              </button>
                                            )}
                                            {story && story.scenario_generated ? (
                                              <button
                                                className="view-scenarios-btn small"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleViewScenarios(story);
                                                }}
                                                title="View Generated Scenarios"
                                              >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                  <circle cx="12" cy="12" r="3"></circle>
                                                </svg>
                                                View Scenarios
                                              </button>
                                            ) : (
                                              <button
                                                className="generate-scenarios-btn"
                                                onClick={() => handleGenerateStoryScenarios(story)}
                                                title="Generate BDD Test Scenarios"
                                                disabled={loading}
                                              >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                                                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                                                </svg>
                                                Scenarios
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      </li>
                                      );
                                    })}
                                  </ul>
                                ) : (
                                  <p className="no-items">No stories found for this epic</p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                      <div className="pagination-container">
                          <Pagination
                            page={currentPage}
                            pageSize={pageSize}
                            total={totalEpics}
                            totalPages={totalEpicPages}
                            onPageChange={handlePageChange}
                            onPageSizeChange={setPageSize}
                            pageSizeOptions={pageSizeOptions}
                            loading={loading}
                          />
                        </div>
                    </>
                  ) : (
                    <p className="no-items">No epics found for this project</p>
                  )}
                </div>
              )}

              {activeTab === 'stories' && (
                <div className="stories-list">
                  <div className="pagination-info" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                  {integrationSource !==3 &&  
                    <button
                        ref={syncBtnRef}
                        className="sync-stories-btn"
                          onClick={() => {
                            if (!showEpicDropdown && syncBtnRef.current) {
                              const rect = syncBtnRef.current.getBoundingClientRect();
                              setDropdownPosition({
                                top: rect.bottom + 4,
                                right: window.innerWidth - rect.right,  // distance from right edge of viewport
                              });
                            }
                            setShowEpicDropdown(!showEpicDropdown);
                          }}
                        title="Sync Jira Stories"
                        disabled={loading}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 1l4 4-4 4"></path>
                          <path d="M3 11V9a4 4 0 014-4h14"></path>
                          <path d="M7 23l-4-4 4-4"></path>
                          <path d="M21 13v2a4 4 0 01-4 4H3"></path>
                        </svg>
                        Sync Stories
                      </button>}

                 {showEpicDropdown && createPortal(
                      <div
                        ref={dropdownRef}
                        style={{
                          position: 'fixed',
                          top: dropdownPosition.top,
                          right: dropdownPosition.right,
                          left: 'auto',
                          backgroundColor: 'white',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          padding: '10px',
                          zIndex: 99999,
                          width: '300px',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
                        }}
                      >
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ fontSize: '13px', fontWeight: 500 }}>Select Epic:</label>
                    {/* Custom list instead of native <select> */}
                    <div style={{
                      marginTop: '6px',
                      maxHeight: '250px',
                      overflowY: 'auto',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                    }}>
                      {[{ key: '', summary: 'All Epics' }, ...epics].map((epic, index) => (
                        <div
                          key={epic.key || 'all'}
                          onClick={() => setSelectedEpicForSync(epic.key)}
                          style={{
                            padding: '8px 10px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            backgroundColor: selectedEpicForSync === epic.key
                              ? '#2563eb'
                              : index % 2 === 0 ? '#fff' : '#f9f9f9',
                            color: selectedEpicForSync === epic.key ? '#fff' : '#333',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          onMouseEnter={e => {
                            if (selectedEpicForSync !== epic.key)
                              e.currentTarget.style.backgroundColor = '#f0f4ff';
                          }}
                          onMouseLeave={e => {
                            if (selectedEpicForSync !== epic.key)
                              e.currentTarget.style.backgroundColor =
                                index % 2 === 0 ? '#fff' : '#f9f9f9';
                          }}
                        >
                          {epic.key ? `${epic.key} - ${epic.summary}` : 'All Epics'}
                        </div>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => handleSyncStories(selectedEpicForSync)}
                    disabled={loading}
                    style={{ width: '100%', padding: '8px', marginTop: '4px' }}
                  >
                    Sync {selectedEpicForSync ? 'Selected Epic' : 'All Stories'}
                  </button>
                </div>,
                document.body
              )}
                    </div>
                  </div>
                  {stories.length > 0 ? (
                    <div>

                      <table className="stories-table">
                        <thead>
                          <tr>
                            <th>Key</th>
                            <th>Summary</th>
                            <th>Status</th>
                            <th>Priority</th>
                            <th>Assignee</th>
                            <th>Story Points</th>
                            <th>Epic</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stories.map(story => {
                            const epic = epics.find(e => e.id === story.epicId);
                            const { referenceId, description } = parseSourceReference(story.description);
                            return (
                              <tr key={story.id}>
                                <td>{story.key}</td>
                                <td>
                                  <div className="story-summary-cell">
                                    <div className="rt-story-summary">
                                      <span className="rt-story-summary__text">{story.summary}</span>
                                      <SourceReferenceBadge referenceId={referenceId} />
                                    </div>
                                    <div className="story-description">{description}</div>
                                  </div>
                                </td>
                                <td>
                                  <span className={`status-badge ${getStatusClass(story.status)}`}>{story.status}</span>
                                </td>
                                <td>
                                  <span className={`priority-badge ${getPriorityClass(story.priority)}`}>{story.priority}</span>
                                </td>
                                <td>{story.assignee || 'Unassigned'}</td>
                                <td>{story.storyPoints != null && story.storyPoints !== '' ? story.storyPoints : '—'}</td>
                                <td>
                                  {story.epicKey ? (
                                    <span
                                      className="epic-link"
                                      onClick={() => {
                                        setActiveTab('epics');
                                        const epicToExpand = epics.find((e) => e.key === story.epicKey);
                                        if (epicToExpand) {
                                          setExpandedEpic(epicToExpand.key);
                                        }
                                      }}
                                    >
                                      {story.epicKey}
                                    </span>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                                <td>
                                  <div className="story-action-buttons">
                                    {story && story.case_generated ? (
                                      <button
                                        className="view-tests-btn"
                                        onClick={() => handleViewTests(story)}
                                        title="View Generated Tests"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                          <circle cx="12" cy="12" r="3"></circle>
                                        </svg>
                                        View Tests
                                      </button>
                                    ) : (
                                      <button
                                        className="generate-tests-btn"
                                        onClick={() => handleGenerateTestCases(story, 'story')}
                                        title="Generate AI Test Cases"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"></path>
                                        </svg>
                                        Generate Tests
                                      </button>
                                    )}
                                    {story && story.scenario_generated ? (
                                      <button
                                        className="view-scenarios-btn"
                                        onClick={() => handleViewScenarios(story)}
                                        title="View Generated Scenarios"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                          <circle cx="12" cy="12" r="3"></circle>
                                        </svg>
                                        View Scenarios
                                      </button>
                                    ) : (
                                      <>

                                        <button
                                          className="generate-scenarios-btn"
                                          onClick={() => handleGenerateStoryScenarios(story)}
                                          title="Generate BDD Test Scenarios"
                                          disabled={loading}
                                        >
                                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                                            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                                          </svg>
                                          Generate Scenarios
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      <div className="pagination-container">
                          <Pagination
                            page={currentPage}
                            pageSize={pageSize}
                            total={totalStories}
                            totalPages={totalStoryPages}
                            onPageChange={handlePageChange}
                            onPageSizeChange={setPageSize}
                            pageSizeOptions={pageSizeOptions}
                            loading={loading}
                          />
                        </div>
                    </div>
                  ) : (
                    <p className="no-items">No stories found for this project</p>
                  )}
                </div>
              )}

              {/* Saved Test Cases Section */}
              {savedTestCases.length > 0 && (
                <div className="saved-test-cases">
                  <h3>
                    Generated Test Cases
                    <span className="test-case-count">{savedTestCases.length}</span>
                  </h3>
                  <table className="stories-table">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Source</th>
                        <th>Priority</th>
                        <th>Estimated Time</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {savedTestCases.map((testCase, index) => {
                        const sourceItem = testCase.sourceItem;
                        const formattedDate = formatDateTime(testCase.createdAt);

                        return (
                          <tr key={index}>
                            <td>
                              <div className="story-summary-cell">
                                <div>{testCase.title}</div>
                                <div className="story-description">{testCase.description}</div>
                              </div>
                            </td>
                            <td>
                              <span className="jira-key">
                                {sourceItem.key} ({testCase.sourceItemType})
                              </span>
                            </td>
                            <td>
                              <span className={`priority-badge priority-${testCase.priority.toLowerCase()}`}>
                                {testCase.priority}
                              </span>
                            </td>
                            <td>{testCase.estimatedTime}</td>
                            <td>{formattedDate}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showTestCasesModal && (
        <TestCasesModal
          testCasesData={testCasesData}
          onClose={() => setShowTestCasesModal(false)}
          title="Test Cases"
          projectId={project.id}
          storyId={viewTestsStoryId}
          isServerSide={true}
        />
      )}
    </div>
  );
};

export default ProjectJiraItems;
